import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ShieldCheck, KeyRound, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const [delOpen, setDelOpen] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const changePassword = async () => {
    if (newPwd.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPwd !== confirmPwd) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPwd, new_password: newPwd });
      toast.success("Password updated");
      setOldPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    if (delConfirm !== "DELETE") { toast.error('Type "DELETE" to confirm'); return; }
    setDelBusy(true);
    try {
      await api.delete("/auth/account");
      toast.success("Account deleted");
      localStorage.removeItem("skintrack_token");
      await logout();
      navigate("/");
    } catch (e) {
      toast.error("Could not delete account");
      setDelBusy(false);
    }
  };

  return (
    <AppShell>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-sage-500">Account</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-stone-900 mt-2">Settings</h1>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 bg-white rounded-2xl p-6 border border-stone-100" data-testid="settings-profile">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-sage-500"/>
            <h3 className="font-serif text-2xl text-stone-900">Your account</h3>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-stone-500">Name</p>
              <p className="text-stone-800 mt-1">{user?.name || "—"}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-stone-500">Email</p>
              <p className="text-stone-800 mt-1" data-testid="settings-email">{user?.email}</p>
            </div>
          </div>
        </section>

        <section className="lg:col-span-2 bg-white rounded-2xl p-6 border border-stone-100" data-testid="settings-change-password">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-sage-500"/>
            <h3 className="font-serif text-2xl text-stone-900">Change password</h3>
          </div>
          <div className="mt-5 space-y-4 max-w-md">
            <div>
              <Label htmlFor="old-pwd">Current password</Label>
              <Input id="old-pwd" type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} className="mt-1.5 bg-stone-50 border-stone-200 rounded-xl" data-testid="old-password-input"/>
            </div>
            <div>
              <Label htmlFor="new-pwd">New password</Label>
              <Input id="new-pwd" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-1.5 bg-stone-50 border-stone-200 rounded-xl" data-testid="new-password-input"/>
              <p className="text-xs text-stone-400 mt-1">Minimum 8 characters.</p>
            </div>
            <div>
              <Label htmlFor="confirm-pwd">Confirm new password</Label>
              <Input id="confirm-pwd" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="mt-1.5 bg-stone-50 border-stone-200 rounded-xl" data-testid="confirm-password-input"/>
            </div>
            <Button onClick={changePassword} disabled={busy || !oldPwd || !newPwd || !confirmPwd} className="rounded-full bg-sage-400 hover:bg-sage-500 text-white" data-testid="change-password-btn">
              {busy ? "Updating..." : "Update password"}
            </Button>
          </div>
        </section>

        <section className="lg:col-span-2 bg-white rounded-2xl p-6 border border-rose-100" data-testid="settings-danger">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-500"/>
            <h3 className="font-serif text-2xl text-stone-900">Danger zone</h3>
          </div>
          <p className="text-sm text-stone-600 mt-3 leading-relaxed">
            Permanently delete your account and all data — entries, photos, POEM scores, treatments, diagnoses. This cannot be undone.
          </p>
          <Button onClick={() => setDelOpen(true)} variant="outline" className="mt-4 rounded-full border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="delete-account-btn">
            <Trash2 className="w-4 h-4 mr-2"/>Delete my account
          </Button>
        </section>
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="bg-sand-50">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-stone-900">Delete account?</DialogTitle>
            <DialogDescription className="text-stone-600">
              This will permanently remove all of your entries, photos, POEM scores, and account info. Type <strong className="font-mono text-rose-600">DELETE</strong> to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={delConfirm}
            onChange={(e) => setDelConfirm(e.target.value)}
            placeholder="Type DELETE"
            className="bg-white border-stone-200 rounded-xl"
            data-testid="delete-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDelOpen(false); setDelConfirm(""); }} className="rounded-full" data-testid="delete-cancel-btn">Cancel</Button>
            <Button onClick={deleteAccount} disabled={delBusy || delConfirm !== "DELETE"} className="rounded-full bg-rose-500 hover:bg-rose-600 text-white" data-testid="delete-confirm-btn">
              {delBusy ? "Deleting..." : "Permanently delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
